import {
  ESymbolCategory,
  IAction,
  IActionParameter,
  IDisplayItem,
  IDisplayItemProperty,
  IPropertyDefinition,
  IScreenDesignDictionary,
  ISymbol
} from "@kuika/kuika-cl-model";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import { ICurrentRowSource } from "../../models/interface/action-clipboard";

// A "Current Row" value is a symbol of category `current` whose `diId` is the component the symbol
// picker attached it to. Codegen walks from that component up to its enclosing list and reads that
// list's row, so an action pasted somewhere else keeps reading the SOURCE list's row — while the
// panel still shows `Current Row.<field>` — unless `diId` moves with it (KOA-16658).

const MAX_ANCESTOR_DEPTH: number = 200;
const CALENDAR_ROW_SOURCE_KEY: string = "calendar";

/** The data-binding flags the symbol picker treats as a list whose row Current Row can read. */
export function isRowSourceOptionsProperty(propertyDefinition: IPropertyDefinition | undefined): boolean {
  return (
    propertyDefinition?.isOptionsProp === true ||
    propertyDefinition?.isTableOptionsProp === true ||
    propertyDefinition?.isGoogleMapsOptionsProp === true ||
    propertyDefinition?.isTreeOptionsProp === true ||
    propertyDefinition?.isCalendarOptionsProp === true ||
    propertyDefinition?.isMenuOptionsProp === true ||
    propertyDefinition?.isBreadcrumbOptionsProp === true ||
    propertyDefinition?.isKanbanBoardLaneOptionsProp === true ||
    propertyDefinition?.isKanbanBoardCardOptionsProp === true ||
    propertyDefinition?.isNodeTreeNodeOptionsProp === true
  );
}

function getRowSourceOptions(sd: IScreenDesignContextState, di: IDisplayItem): IDisplayItemProperty[] {
  return (di.item?.properties ?? []).filter((property: IDisplayItemProperty): boolean =>
    isRowSourceOptionsProperty(sd.getPropertyDefinition(di.item, property))
  );
}

function getRowBinding(sd: IScreenDesignContextState, repeater: IDisplayItem): ISymbol | undefined {
  const options: IDisplayItemProperty[] = getRowSourceOptions(sd, repeater);
  // The same pick generateCurrentRow makes: a Maps can list its waypoints first, but its rows are the markers.
  const isMapsWaypoints: boolean = repeater.item.name === "Maps" && options[0]?.name === "waypoints";
  const binding: IDisplayItemProperty | undefined = isMapsWaypoints ? options[1] : options[0];
  return binding?.symbol;
}

function getRepeaterKeyPart(sd: IScreenDesignContextState, repeater: IDisplayItem, isOutermost: boolean): string {
  const binding: ISymbol | undefined = getRowBinding(sd, repeater);
  if (isOutermost && binding?.actionDefinitionId) {
    return `${binding.actionDefinitionId}|${binding.actionDefinitionObjectPath ?? ""}|${binding.resultActionId ?? ""}`;
  }
  if (!isOutermost && binding?.category === ESymbolCategory.current && binding.fieldName) {
    return `.${binding.fieldName}`;
  }
  // Data this cannot name (a screen input, a bag, a fixed list…) is still the same rows inside this
  // one list, so a paste within it keeps its value; it matches no other list.
  return `#${sd.sdId ?? ""}:${repeater.id}`;
}

/**
 * Identity of the rows a component's Current Row reads: two components with the same key read the
 * same row shape from the same data, so a Current Row value can move between them. Undefined when
 * the component reads no rows, or reads Kanban lanes / cards, whose row values this does not model.
 */
export function getCurrentRowSourceKey(sd: IScreenDesignContextState, kuikaId: number): string | undefined {
  const dictionary: IScreenDesignDictionary | undefined = sd.screenDesignDictionary;
  const host: IDisplayItem | undefined = dictionary?.[kuikaId];
  if (!dictionary || !host?.item) return undefined;
  // generateCurrentRow: a Calendar's Current Row is its own date / event — the same fields on every Calendar.
  if (host.item.name === "Calendar") return CALENDAR_ROW_SOURCE_KEY;

  const repeaters: IDisplayItem[] = [];
  let current: IDisplayItem = host;
  for (let depth = 0; depth < MAX_ANCESTOR_DEPTH && current.parentId != null; depth++) {
    const parent: IDisplayItem | undefined = dictionary[current.parentId];
    if (!parent?.item) break;
    if (getRowSourceOptions(sd, parent).length > 0) repeaters.unshift(parent);
    current = parent;
  }
  if (repeaters.length === 0) return undefined;
  if (repeaters.some((repeater: IDisplayItem): boolean => repeater.item.name === "KanbanBoard")) return undefined;

  return repeaters
    .map((repeater: IDisplayItem, index: number): string => getRepeaterKeyPart(sd, repeater, index === 0))
    .join(">");
}

function isCurrentRowSymbol(node: object): node is ISymbol {
  return "category" in node && node.category === ESymbolCategory.current;
}

/**
 * Every Current Row value inside an action: parameters, screen inputs, conditions, conditional
 * values, and the If / Else / ForEach / Recurring / MQTT actions nested in it. A structural walk on
 * purpose — KOA-5067 listed the one slot it knew and missed a Navigate's screen inputs.
 */
function collectCurrentRowSymbols(node: unknown, found: ISymbol[]): ISymbol[] {
  if (Array.isArray(node)) {
    node.forEach((item: unknown): void => {
      collectCurrentRowSymbols(item, found);
    });
    return found;
  }
  if (typeof node !== "object" || node === null) return found;
  if (isCurrentRowSymbol(node)) found.push(node);
  Object.values(node).forEach((value: unknown): void => {
    collectCurrentRowSymbols(value, found);
  });
  return found;
}

function toKuikaId(value: unknown): number | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  const id: number = Number(value);
  return Number.isFinite(id) ? id : undefined;
}

/** A ForEach loop-item value: bound to its loop action, not to a list, so no list rule applies to it. */
function isLoopItemSymbol(symbol: ISymbol): boolean {
  return Boolean(symbol.parentActionId);
}

/** The "no value" a fresh parameter starts with — never a type default (0 / false / "") that would ship looking like data. */
function clearCurrentRowSymbol(symbol: ISymbol): void {
  symbol.category = ESymbolCategory.fixed;
  symbol.isFixed = true;
  symbol.label = "Fixed Value";
  symbol.description = "";
  symbol.fixedValue = undefined;
  delete symbol.diId;
  delete symbol.fieldName;
  delete symbol.fieldType;
  delete symbol.currentType;
  delete symbol.currentCardinality;
  delete symbol.converterOfType;
  delete symbol.converterOfCardinality;
}

/** Copy side: which rows each Current Row value of `action` reads, on the screen it is copied from. */
export function collectCurrentRowSources(action: IAction, sd: IScreenDesignContextState): ICurrentRowSource[] {
  const sources: ICurrentRowSource[] = [];
  collectCurrentRowSymbols(action, []).forEach((symbol: ISymbol): void => {
    const diId: number | undefined = toKuikaId(symbol.diId);
    if (isLoopItemSymbol(symbol) || diId === undefined) return;
    if (sources.some((source: ICurrentRowSource): boolean => source.diId === diId)) return;
    const rowSourceKey: string | undefined = getCurrentRowSourceKey(sd, diId);
    if (rowSourceKey !== undefined) sources.push({ diId, rowSourceKey });
  });
  return sources;
}

/**
 * Paste side: moves each Current Row value of the pasted `action` onto `hostKuikaId` when the host
 * reads the same rows the value was copied from, and clears it otherwise — a pasted action never
 * keeps reading another list's row. Loop-item values are left as they are.
 */
export function rebindPastedCurrentRowSymbols(
  action: IAction,
  sd: IScreenDesignContextState,
  hostKuikaId: number | undefined,
  sources: ICurrentRowSource[] | undefined
): void {
  const hostKey: string | undefined = hostKuikaId === undefined ? undefined : getCurrentRowSourceKey(sd, hostKuikaId);
  collectCurrentRowSymbols(action, []).forEach((symbol: ISymbol): void => {
    if (isLoopItemSymbol(symbol)) return;
    const diId: number | undefined = toKuikaId(symbol.diId);
    const source: ICurrentRowSource | undefined = sources?.find(
      (candidate: ICurrentRowSource): boolean => candidate.diId === diId
    );
    if (hostKey !== undefined && source?.rowSourceKey === hostKey) {
      symbol.diId = hostKuikaId;
    } else {
      clearCurrentRowSymbol(symbol);
    }
  });
}

/**
 * KOA-5067 cleared every top-level Current Row parameter on the "+ PASTE" and If / Else paste
 * buttons. Row values now go through `rebindPastedCurrentRowSymbols`; this keeps that clearing for
 * the one kind it leaves alone, so those two buttons treat loop-item parameters exactly as before.
 */
export function clearPastedLoopItemParameters(action: IAction): void {
  (action.parameters ?? []).forEach((parameter: IActionParameter): void => {
    const symbol: ISymbol | undefined = parameter.symbol;
    if (symbol && isCurrentRowSymbol(symbol) && isLoopItemSymbol(symbol)) clearCurrentRowSymbol(symbol);
  });
}
